import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

export interface PageVisit {
  id?: string;
  userId?: string;
  userEmail?: string;
  page: string;
  path: string;
  referrer: string;
  timestamp: Date;
  sessionId: string;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  duration?: number;
}

export interface AnalyticsEvent {
  id?: string;
  userId?: string;
  userEmail?: string;
  eventType: string;
  eventData: Record<string, any>;
  timestamp: Date;
  sessionId: string;
}

export interface AnalyticsSummary {
  totalVisits: number;
  uniqueVisitors: number;
  pageViews: Record<string, number>;
  topPages: { page: string; visits: number }[];
  visitsByDay: { date: string; visits: number }[];
  events: { type: string; count: number }[];
  cancellations: CancellationEvent[];
}

export interface CancellationEvent {
  id?: string;
  userId: string;
  userEmail: string;
  plan: string;
  reason?: string;
  feedback?: string;
  timestamp: Date;
  subscriptionStartDate?: Date;
  totalPaid?: number;
}

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Track page visit
export const trackPageVisit = async (
  page: string,
  path: string,
  userId?: string,
  userEmail?: string
): Promise<void> => {
  try {
    const visit: PageVisit = {
      userId,
      userEmail,
      page,
      path,
      referrer: document.referrer,
      timestamp: new Date(),
      sessionId: getSessionId(),
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
    };

    await axios.post(`${API_BASE_URL}/api/analytics/pagevisit`, visit);
  } catch (error) {
    console.error('Failed to track page visit:', error);
  }
};

// Track custom event
export const trackEvent = async (
  eventType: string,
  eventData: Record<string, any>,
  userId?: string,
  userEmail?: string
): Promise<void> => {
  try {
    const event: AnalyticsEvent = {
      userId,
      userEmail,
      eventType,
      eventData,
      timestamp: new Date(),
      sessionId: getSessionId(),
    };

    await axios.post(`${API_BASE_URL}/api/analytics/event`, event);
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

// Track subscription cancellation
export const trackCancellation = async (
  userId: string,
  userEmail: string,
  plan: string,
  reason?: string,
  feedback?: string
): Promise<void> => {
  try {
    const cancellation: CancellationEvent = {
      userId,
      userEmail,
      plan,
      reason,
      feedback,
      timestamp: new Date(),
    };

    await axios.post(`${API_BASE_URL}/api/analytics/cancellation`, cancellation);
  } catch (error) {
    console.error('Failed to track cancellation:', error);
  }
};

// Get analytics summary (admin only)
export const getAnalyticsSummary = async (
  startDate?: Date,
  endDate?: Date
): Promise<AnalyticsSummary> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate.toISOString());
  if (endDate) params.append('endDate', endDate.toISOString());

  const response = await axios.get(`${API_BASE_URL}/api/analytics/summary?${params.toString()}`);
  return response.data;
};

// Get page visits (admin only)
export const getPageVisits = async (
  limit: number = 100,
  page: string | null = null
): Promise<PageVisit[]> => {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  if (page) params.append('page', page);

  const response = await axios.get(`${API_BASE_URL}/api/analytics/pagevisits?${params.toString()}`);
  return response.data;
};

// Get events (admin only)
export const getEvents = async (
  limit: number = 100,
  eventType: string | null = null
): Promise<AnalyticsEvent[]> => {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  if (eventType) params.append('eventType', eventType);

  const response = await axios.get(`${API_BASE_URL}/api/analytics/events?${params.toString()}`);
  return response.data;
};

// Get cancellations (admin only)
export const getCancellations = async (
  limit: number = 100
): Promise<CancellationEvent[]> => {
  const response = await axios.get(`${API_BASE_URL}/api/analytics/cancellations?limit=${limit}`);
  return response.data;
};

const analyticsService = {
  trackPageVisit,
  trackEvent,
  trackCancellation,
  getAnalyticsSummary,
  getPageVisits,
  getEvents,
  getCancellations,
};

export default analyticsService;
